import { EFeatureFlag } from 'constants/featureFlags';

import { datadogRum } from '@datadog/browser-rum';
import { getInstance, getPrecomputedInstance, IAssignmentDetails } from '@eppo/js-client-sdk';
import { OpenFeature } from '@openfeature/web-sdk';

import { useOpenFeatureInitialized } from 'components/feature-flag/EppoFeatureFlagProvider';

import { augmentSubjectAttributes } from 'lib/augment-subject-attributes';

import { useUser } from 'hooks/useUser';

// Please check with FF team before modifying.
export const shouldInitializePrecomputedClient = true;

export const shouldInitializeOpenFeatureClient = true;
export const shouldWaitForOpenFeatureClientInitialization = true;
export const shouldInitializeDatadogProvider = true;

// Log to console if false
const shouldLogFlagComparisonToDatadog = true;

// Datadog/OpenFeature is authoritative after the Eppo flag migration.
// Keep evaluating Eppo temporarily so flags that have not yet been migrated can
// continue to use their legacy value until they are created in Datadog.
export const shouldPreferEppoOverOpenFeature = false;

export const useOpenFeatureClientForFeatureFlags = true;

if (useOpenFeatureClientForFeatureFlags && !shouldInitializeOpenFeatureClient) {
  console.warn(
    'useOpenFeatureClientForFeatureFlags is true but shouldInitializeOpenFeatureClient is false; legacy Eppo Client will be used.',
  );
}

function logItem(logToDatadog: boolean, action_name: string, action_data: object) {
  if (!logToDatadog) {
    console.log(action_name, {
      ...action_data,
      timestamp: new Date().toISOString(),
    });
  } else {
    datadogRum.addAction(action_name, {
      ...action_data,
      timestamp: new Date().toISOString(),
    });
  }
}

type FlagType = 'boolean' | 'string' | 'integer' | 'numeric' | 'object';

/**
 * Generic helper function to handle flag assignment logic with both legacy and OpenFeature clients.
 * This function implements the common pattern used across all flag assignment functions.
 *
 * Strategy:
 * - If flag exists in OpenFeature, use its value
 * - If flag only exists in Eppo, use Eppo value
 * - If flag exists in neither, return default value
 *
 * @template T The type of the flag value (boolean, string, number, object)
 * @param flagType The type of flag ('boolean', 'string', 'integer', 'numeric', 'object')
 * @param flagKey The feature flag key
 * @param subjectAttributes Additional attributes for the subject
 * @param defaultValue The default value to return if the flag is not found
 * @returns The flag assignment value
 */
function useFlagAssignmentInternal<T extends string | number | boolean | object>(
  flagType: FlagType,
  flagKey: EFeatureFlag,
  subjectAttributes: Record<string, string | number>,
  defaultValue: T,
): T {
  const userId = augmentSubjectAttributes(subjectAttributes, useUser());
  const subjectKey = String(userId);

  // Subscribe to OpenFeature provider status from context
  // This will cause re-renders when the provider becomes ready
  const isOpenFeatureReady = useOpenFeatureInitialized();

  // If we're waiting for OpenFeature initialization and it's not ready yet, use legacy assignment
  const shouldWaitForOpenFeature =
    shouldWaitForOpenFeatureClientInitialization && !isOpenFeatureReady;

  // Get legacy assignment based on flag type
  const eppoClient = getInstance();
  let legacyAssignment = defaultValue as T;
  let legacyFlagExists = true;
  let assignmentDetails: IAssignmentDetails<T>;

  // Use get<T>AssignmentDetails() methods to get error information and determine if flag exists
  switch (flagType) {
    case 'boolean':
      assignmentDetails = eppoClient.getBooleanAssignmentDetails(
        flagKey,
        subjectKey,
        subjectAttributes,
        defaultValue as boolean,
      ) as IAssignmentDetails<T>;
      break;
    case 'string':
      assignmentDetails = eppoClient.getStringAssignmentDetails(
        flagKey,
        subjectKey,
        subjectAttributes,
        defaultValue as string,
      ) as IAssignmentDetails<T>;
      break;
    case 'integer':
      assignmentDetails = eppoClient.getIntegerAssignmentDetails(
        flagKey,
        subjectKey,
        subjectAttributes,
        defaultValue as number,
      ) as IAssignmentDetails<T>;
      break;
    case 'numeric':
      assignmentDetails = eppoClient.getNumericAssignmentDetails(
        flagKey,
        subjectKey,
        subjectAttributes,
        defaultValue as number,
      ) as IAssignmentDetails<T>;
      break;
    case 'object':
      assignmentDetails = eppoClient.getJSONAssignmentDetails(
        flagKey,
        subjectKey,
        subjectAttributes,
        defaultValue as object,
      ) as IAssignmentDetails<T>;
      break;
  }

  legacyAssignment = assignmentDetails.variation;
  if (assignmentDetails.evaluationDetails.flagEvaluationCode === 'FLAG_UNRECOGNIZED_OR_DISABLED') {
    legacyFlagExists = false;
  }

  // OpenFeature client integration
  try {
    const ofClient = OpenFeature.getClient();

    // Check if OpenFeature provider is ready before evaluating
    // If we're waiting for initialization and provider is not ready, return legacy assignment
    if (shouldWaitForOpenFeature) {
      return legacyAssignment;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let openFeatureDetails: any;

    // Use get<T>Details() methods to get error information and determine if flag exists
    // Pass subjectAttributes as the evaluation context to ensure all targeting attributes are included
    switch (flagType) {
      case 'boolean':
        openFeatureDetails = ofClient.getBooleanDetails(
          flagKey,
          defaultValue as boolean,
          subjectAttributes,
        );
        break;
      case 'string':
        openFeatureDetails = ofClient.getStringDetails(
          flagKey,
          defaultValue as string,
          subjectAttributes,
        );
        break;
      case 'integer':
      case 'numeric':
        openFeatureDetails = ofClient.getNumberDetails(
          flagKey,
          defaultValue as number,
          subjectAttributes,
        );
        break;
      case 'object':
        openFeatureDetails = ofClient.getObjectDetails(
          flagKey,
          defaultValue as any,
          subjectAttributes,
        );
        break;
    }

    const openFeatureAssignment = openFeatureDetails.value as T;
    // Flag exists in OpenFeature if there's no error code (or if error code is not FLAG_NOT_FOUND)
    const openFeatureFlagExists =
      !openFeatureDetails.errorCode || openFeatureDetails.errorCode !== 'FLAG_NOT_FOUND';

    // Do not include the legacy value when reporting flags that have not been migrated.
    if (legacyFlagExists && !openFeatureFlagExists) {
      logItem(shouldLogFlagComparisonToDatadog, 'feature_flag_config_mismatch_eppo_only', {
        flagKey,
      });
    }

    // Decision logic:
    // 1. If the flag exists in OpenFeature, use its value. The legacy preference
    //    remains as a rollback switch during the migration.
    // 2. If the flag doesn't exist in OpenFeature but exists in Eppo, use Eppo.
    // 3. If the flag doesn't exist in either, return the default value.

    if (openFeatureFlagExists) {
      // Optional rollback behavior: prefer Eppo when both providers disagree.
      if (
        shouldPreferEppoOverOpenFeature &&
        legacyFlagExists &&
        legacyAssignment !== openFeatureAssignment
      ) {
        return legacyAssignment;
      }
      return openFeatureAssignment;
    }
  } catch (error) {
    console.warn(`OpenFeature client not available for ${flagType} flag:`, flagKey, error);
    // Fall through to return legacyAssignment
  }
  // If the flag doesn't exist in OpenFeature, retain the Eppo value as a
  // temporary migration fallback (or the default if neither provider has it).
  return legacyAssignment;
}

/**
 * This method is meant to pull non-randomized feature flag configurations.
 * It randomizes based on userId, so if a flag has randomization, different users within the same company may get different results.
 */
export const useBoolFlagAssignment = (
  flagKey: EFeatureFlag,
  subjectAttributes = {} as Record<string, string | number>,
  defaultValue = false,
): boolean => {
  return useFlagAssignmentInternal<boolean>('boolean', flagKey, subjectAttributes, defaultValue);
};

/**
 * This method is meant to pull non-randomized feature flag configurations.
 * It randomizes based on userId, so if a flag has randomization, different users within the same company may get different results.
 */
export const useStringFlagAssignment = (
  flagKey: EFeatureFlag,
  subjectAttributes: Record<string, string | number>,
  defaultValue: string,
): string => {
  return useFlagAssignmentInternal<string>('string', flagKey, subjectAttributes, defaultValue);
};

/**
 * Shortcut for checking legacy "ON/OFF" feature flags (string-valued flags used as boolean switches).
 *
 * @deprecated Use useBoolFlagAssignment instead, together with a boolean-typed flag.
 * This method is only to be used to support legacy string-valued flags.
 */
export function useStringFlagOn(
  flagKey: EFeatureFlag,
  subjectAttributes: Record<string, string | number> = {},
): boolean {
  const assignment = useFlagAssignmentInternal<string>('string', flagKey, subjectAttributes, 'off');
  return ['on', 'true'].includes(assignment.toString().toLowerCase());
}

export const useIntFlagAssignment = (
  flagKey: EFeatureFlag,
  subjectAttributes: Record<string, string | number>,
  defaultValue: number,
): number => {
  return useFlagAssignmentInternal<number>('integer', flagKey, subjectAttributes, defaultValue);
};

export const useNumericFlagAssignment = (
  flagKey: EFeatureFlag,
  subjectAttributes: Record<string, string | number>,
  defaultValue: number,
): number => {
  return useFlagAssignmentInternal<number>('numeric', flagKey, subjectAttributes, defaultValue);
};

export const useJsonFlagAssignment = <T extends object = object>(
  flagKey: EFeatureFlag,
  subjectAttributes: Record<string, string | number> = {},
  defaultValue: T,
): T => {
  return useFlagAssignmentInternal<T>('object', flagKey, subjectAttributes, defaultValue);
};

export function usePrecomputedBanditAction(
  flagKey: string,
  defaultValue: string,
): { variation: string; action: string | null } {
  const eppoPrecomputedClient = getPrecomputedInstance();
  const precomputedAssignment = eppoPrecomputedClient.getBanditAction(flagKey, defaultValue);

  // if (shouldLogDebugPrecomputedClient) {
  //   console.debug('usePrecomputedBanditAction', {
  //     flagKey,
  //     precomputedAssignment,
  //   });
  // }

  return precomputedAssignment;
}
